<template>
  <v-layout class="d-flex flex-column overflow-visible pl-1" style="border-bottom: 1px solid #E5E3E1;">
    <v-row class="py-4 align-center flex-wrap">
      <!-- Type name -->
      <v-col cols="1">
        <v-btn
          icon="mdi-drag-horizontal-variant"
          class="table-btn sortHandle"
          tabindex="-1"
          :disabled="$store.state.isWorkflowDisabled"
        />
      </v-col>
      <v-col cols="2" class="d-flex align-center justify-start gap-x-4">
        <workflow-settings-requirement-type-color-picker
          :color="requirementType.color && requirementType.color.rgb"
          variant="cards"
          @change="changeColor"
          :disabled="$store.state.isWorkflowDisabled"
        />
        <div class="d-flex flex-column">
          <quick-rename-input
            class="card-type-rename-input"
            :value="requirementType.title"
            :disabled="$store.state.isWorkflowDisabled"
            @onChange="blurField($event)"
          />
          <div style="padding: 4px">
            <div v-if="!showDescription" class="show-hide-description text-light-blue expension-btn"
                 @click="openDescription">
              Show description
              <v-icon size="x-small" color="light-blue">chevron_right</v-icon>
            </div>
            <div v-else class="show-hide-description text-light-blue expension-btn" @click="closeDescription">
              Hide description
              <v-icon size="x-small" color="light-blue">chevron_right</v-icon>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Show in all cards -->
      <v-col cols="2" class="d-flex justify-center py-0">
        <v-tooltip location="top">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="d-flex align-center">
              <v-btn
                :icon="requirementType.showInBacklog ? 'mdi-eye' : 'mdi-eye-off'"
                style="box-shadow: none; font-size: 16px"
                class="eye-btn justify-center ma-0 pa-0"
                @click="() => toggleShowInBacklog(!requirementType.showInBacklog)"
                :disabled="$store.state.isWorkflowDisabled"
              ></v-btn>
            </div>
          </template>
          <span v-if="!$store.state.isWorkflowDisabled">View in backlog</span>
        </v-tooltip>
      </v-col>

      <!-- Use AI -->
      <v-col cols="1" class="d-flex justify-center py-0">
        <v-checkbox
          :model-value="requirementType.generateWithAI"
          @update:model-value="saveGenerateCardWithAI(requirementType)"
          hide-details
          :disabled="$store.state.isWorkflowDisabled"
        />
      </v-col>

      <!-- Command -->
      <v-col cols="2" class="d-flex justify-center py-0">
        <v-checkbox
          density="compact"
          class="ma-0 pa-0 type-checkbox-column"
          hide-details
          :model-value="requirementType.triggerType"
          @update:model-value="toggleTriggerType"
          false-icon="mdi-radiobox-blank"
          true-icon="mdi-radiobox-marked"
          :disabled="$store.state.isWorkflowDisabled"
        />
      </v-col>

      <!-- Aggregate -->
      <v-col cols="1" class="d-flex justify-center py-0">
        <v-checkbox
          density="compact"
          class="ma-0 pa-0 type-checkbox-column"
          hide-details
          :model-value="requirementType.aggregateType"
          @update:model-value="toggleAggregateType"
          false-icon="mdi-radiobox-blank"
          true-icon="mdi-radiobox-marked"
          :disabled="$store.state.isWorkflowDisabled"
        />
      </v-col>

      <!-- Read Model -->
      <v-col cols="2" class="d-flex justify-center py-0">
        <v-checkbox
          density="compact"
          class="ma-0 pa-0 type-checkbox-column"
          hide-details
          :model-value="requirementType.queryType"
          @update:model-value="toggleQueryType"
          false-icon="mdi-radiobox-blank"
          true-icon="mdi-radiobox-marked"
          :disabled="$store.state.isWorkflowDisabled"
        />
      </v-col>

      <!-- Default -->
      <v-col cols="1" class="d-flex justify-center py-0">
        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <span v-bind="props">
              <v-btn size="small" variant="text" icon class="ml-5 mr-0" @click="deleteRequirementType(requirementType.id)"
                     :disabled="$store.state.isWorkflowDisabled">
                <v-icon icon="mdi-trash-can-outline"></v-icon>
              </v-btn>
            </span>
          </template>
          <span>Delete type</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-row class="my-0">
      <v-col class="pt-0 pb-0">
        <v-expand-transition>
          <div v-show="showDescription">
            <div class="card rounded-lg my-2 mr-2">
              <textarea
                v-model="descriptionModel"
                rows="2"
                class="card-description-textarea pa-2"
                ref="textareaElement"
                @focus="computeHeight"
                @keydown.exact="computeHeight" @keydown.enter.exact.prevent="submitDescriptionChange"
                @keydown.alt.enter.exact.prevent="handleDescriptionAltEnter"
                @keydown.27.stop="cancelDescriptionChange()"
                @cut="computeHeight" @paste="computeHeight" @drop="computeHeight"
                :disabled="$store.state.isWorkflowDisabled"
              />

              <v-layout justify-end class="pa-1 d-flex align-center">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-icon color="#61676E" size="20.5" v-bind="props">mdi-information-outline</v-icon>
                  </template>
                  <div style="width: 470px;">
                    Please provide a clear and concise description using standard terminology,
                    ensuring that you clearly state the key details about the new type to help
                    the AI generate accurate content
                  </div>
                </v-tooltip>

                <v-btn rounded="lg" size="small" variant="plain" class="ma-0 mx-2 action-button"
                       :disabled="descriptionModel === requirementType.description" @click="cancelDescriptionChange">
                  Cancel
                </v-btn>

                <v-btn rounded="lg" size="small" variant="flat" color="#363a40" class="ma-0 action-button"
                       :disabled="descriptionModel === requirementType.description" @click="submitDescriptionChange">Save
                </v-btn>
              </v-layout>
            </div>
          </div>
        </v-expand-transition>
      </v-col>
    </v-row>
  </v-layout>
</template>

<script>
import store from '@/store'
import QuickRenameInput from '@/components/QuickRenameInput.vue'
import WorkflowSettingsRequirementTypeColorPicker from '@/components/WorkflowSettingsRequirementTypeColorPicker'
import requirementTypesApi from '@/api/requirementTypesApi'
import { mapState } from 'vuex'

export default {
  components: {
    QuickRenameInput,
    WorkflowSettingsRequirementTypeColorPicker
  },

  props: {
    requirementType: Object
  },

  data () {
    return {
      showDescription: false,
      descriptionModel: ''
    }
  },

  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow
    })
  },

  methods: {
    blurField (event) {
      this.applyTitleChange(event)
    },
    async _emitUpdate (payload) {
      await requirementTypesApi.updateRequirementType(this.$route.params.id, this.requirementType, payload)
      await this.$store.dispatch('workflow/refreshCardTypes', { input: { workflowId: this.$route.params.id } })
    },

    applyTitleChange (ev) {
      const newTitle = ev.target.innerText
      const payload = { title: newTitle }
      if (newTitle !== this.requirementType.title) {
        this._emitUpdate(payload)
      }
    },

    async changeColor (color) {
      await this._emitUpdate({ color })
      await store.dispatch('workflow/getWorkflow', this.$route.params)
    },

    toggleShowInBacklog (value) {
      const payload = {
        showInBacklog: Boolean(value)
      }
      this._emitUpdate(payload)
    },

    toggleTriggerType () {
      const payload = {
        triggerType: !this.requirementType.triggerType,
        queryType: false,
        aggregateType: false,
        showInBacklog: !!this.requirementType.triggerType
      }
      this._emitUpdate(payload)
    },

    toggleQueryType () {
      const payload = {
        queryType: !this.requirementType.queryType,
        triggerType: false,
        aggregateType: false,
        showInBacklog: !!this.requirementType.queryType
      }
      this._emitUpdate(payload)
    },

    toggleAggregateType () {
      const payload = {
        aggregateType: !this.requirementType.aggregateType,
        triggerType: false,
        queryType: false,
        showInBacklog: !!this.requirementType.aggregateType
      }
      this._emitUpdate(payload)
    },

    saveGenerateCardWithAI (requirementType) {
      const payload = {
        generateWithAI: !requirementType.generateWithAI
      }
      this._emitUpdate(payload)
    },

    computeHeight () {
      if (!this.$refs.textareaElement) {
        return
      }

      const { textareaElement } = this.$refs

      this.$nextTick(() => {
        setImmediate(() => {
          textareaElement.style.height = 'auto'
          textareaElement.style.height = `${textareaElement.scrollHeight}px`
        })
      })
    },

    openDescription () {
      this.descriptionModel = this.requirementType.description || ''
      this.showDescription = true

      setTimeout(() => {
        if (this.$refs.textareaElement) {
          this.$refs.textareaElement.focus()
        }
      }, 50)
    },

    closeDescription () {
      this.showDescription = false
    },

    handleDescriptionAltEnter (event) {
      const { textareaElement } = this.$refs
      const startPosition = textareaElement.selectionStart
      const endPosition = textareaElement.selectionEnd
      const result = [
        this.descriptionModel.slice(0, startPosition),
        '\n',
        this.descriptionModel.slice(endPosition)
      ].join('')

      // update textarea value
      this.descriptionModel = result

      // set cursor position
      this.$nextTick(() => {
        textareaElement.selectionEnd = endPosition + 1
      })
    },

    cancelDescriptionChange () {
      this.descriptionModel = this.requirementType.description || ''
      this.$refs.textareaElement.blur()
      this.closeDescription()
    },

    submitDescriptionChange (event) {
      this._emitUpdate({ description: this.descriptionModel })
      event.target.blur()
    },

    async deleteRequirementType (id) {
      const workflowId = this.$route.params.id
      await store.dispatch('requirementTypes/deleteRequirementType', { id, workflowId })
    }
  }
}
</script>

<style scoped>
.card-type-rename-input {
  width: 100%;
}

.type-checkbox-column {
  justify-content: center;
}

.type-title {
  height: 28px;
  color: #1D1F22;
  font-size: 16px;
  text-align: left;
  line-height: 17.6px;
}

.show-hide-description {
  font-size: 13px;
  text-align: left;
  line-height: 14.3px;
}

.type-title-input-container input {
  min-width: 0;
  box-sizing: border-box;
  height: 28px;
  line-height: 28px;
  outline: none;
  border: none;
  border-bottom: 2px solid currentColor;
}

textarea {
  width: 100%;
  resize: none;
  outline: none;
  box-sizing: border-box;
  display: block;
  overflow: hidden;
  min-height: 40px;
}

.action-button {
  border-radius: 6px !important;
  min-width: 60px;
}

.expension-btn {
  cursor: pointer;
  user-select: none;
}

:deep(.v-select__menu-icon) {
  display: none !important;
}

.sortHandle {
  cursor: move;
  box-shadow: none !important;
  font-size: 16px !important;
}

.gap-x-4 {
  column-gap: 12px;
}

.eye-btn :deep(.v-btn__overlay) {
  background: none !important;
}
</style>
